import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MagnifyingGlass, ListChecks } from "@phosphor-icons/react";
import { AdminHeader } from "@/components/admin/parts";
import { ClaimDrawer } from "@/components/admin/wi/ClaimDrawer";
import { Card, Empty, Pill, aud, num, selectCls } from "@/components/admin/wi/ui";
import { adminApi } from "@/lib/api";

export default function WiReview() {
  const [params, setParams] = useSearchParams();
  const flag = params.get("flag") || "";
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => {
    adminApi.get("/admin/wi/review", { params: { flag, q, page } }).then(({ data }) => setData(data)).catch(() => setData({ rows: [], flags: [], total: 0 }));
  }, [flag, q, page]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const setFlag = (f) => { setPage(0); setParams(f ? { flag: f } : {}); };

  return (
    <div data-testid="wi-review-page">
      <AdminHeader title="Review Queue" subtitle="Incomplete, ambiguous or duplicate historical records. Correct them or document the exception." />
      <div className="p-8 space-y-5">
        <div className="flex flex-wrap gap-2" data-testid="wi-review-flags">
          <button onClick={() => setFlag("")} className={`text-xs rounded-full px-3 py-1.5 border ${!flag ? "border-[#73bf44] text-white bg-[#73bf44]/10" : "border-neutral-800 text-neutral-400 hover:text-white"}`} data-testid="wi-review-flag-all">All</button>
          {(data?.flags || []).map((f) => (
            <button key={f.key} onClick={() => setFlag(f.key)} data-testid={`wi-review-flag-${f.key}`}
              className={`text-xs rounded-full pl-3 pr-1.5 py-1 border inline-flex items-center gap-2 ${flag === f.key ? "border-[#73bf44] text-white bg-[#73bf44]/10" : "border-neutral-800 text-neutral-400 hover:text-white"}`}>
              {f.label} <Pill tone="amber">{f.count}</Pill>
            </button>
          ))}
        </div>
        <div className="relative max-w-sm">
          <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
          <input value={q} onChange={(e) => { setPage(0); setQ(e.target.value); }} placeholder="Search chassis, OTMA, customer, dealer" className={`${selectCls} w-full pl-9`} data-testid="wi-review-search" />
        </div>
        <Card title={`Records needing attention${data ? ` · ${num(data.total)}` : ""}`} icon={ListChecks}>
          {!data ? <div className="h-40 animate-pulse" /> : !data.rows.length ? <Empty>Nothing to review here.</Empty> : (
            <>
              <table className="w-full text-xs">
                <thead className="text-[0.6rem] uppercase tracking-wider text-neutral-500 text-left">
                  <tr className="border-b border-neutral-800"><th className="py-2 pr-3">Claim</th><th className="py-2 pr-3">Model / dealer</th><th className="py-2 pr-3">Outcome</th><th className="py-2 pr-3 text-right">Amount</th><th className="py-2">Issues</th></tr>
                </thead>
                <tbody>
                  {data.rows.map((c) => (
                    <tr key={c.id} onClick={() => setOpen(c.id)} className="border-b border-neutral-900 hover:bg-white/[0.03] cursor-pointer align-top" data-testid="wi-review-row">
                      <td className="py-2.5 pr-3 whitespace-nowrap"><div className="font-mono text-neutral-100">{c.otma || c.chassis || "—"}</div><div className="text-neutral-600">{c.chassis} · {c.claim_date}</div></td>
                      <td className="py-2.5 pr-3 text-neutral-400">{c.model || c.model_family}<div className="text-neutral-600">{c.dealer}</div></td>
                      <td className="py-2.5 pr-3 capitalize text-neutral-300">{c.human_outcome}<div className="text-neutral-600 normal-case">{c.source === "w2" ? "Warranty 2.0" : "Register"}</div></td>
                      <td className="py-2.5 pr-3 text-right tabular-nums text-[#d9b45b]">{aud(c.final_amount, 2)}</td>
                      <td className="py-2.5"><div className="flex flex-wrap gap-1">{c.flags.map((f) => <Pill key={f} tone="amber">{(data.flags.find((x) => x.key === f) || {}).label || f}</Pill>)}</div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex items-center justify-end gap-3 pt-3 text-xs text-neutral-500">
                <button disabled={page === 0} onClick={() => setPage(page - 1)} className="hover:text-white disabled:opacity-30" data-testid="wi-review-prev">Previous</button>
                <span>Page {page + 1} of {Math.max(1, Math.ceil(data.total / data.page_size))}</span>
                <button disabled={(page + 1) * data.page_size >= data.total} onClick={() => setPage(page + 1)} className="hover:text-white disabled:opacity-30" data-testid="wi-review-next">Next</button>
              </div>
            </>
          )}
        </Card>
      </div>
      {open && <ClaimDrawer id={open} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
}
